// Public API for the analytics layer.
//
//   import { track, initAnalytics } from "@analytics"
//   track("report_sent", { report_type: "pricing" })
//
// Design contract (PRD §4.1): module singleton (no React context, so firing an event never
// re-renders anything), fire-and-forget (never awaits), and FAIL-SILENT — every public
// entry point swallows its own errors so a bug here can never throw into business code.

import { config } from "./config"
import { ensureSession, touchSession, getCurrent } from "./session"
import { buildEnvelope, setPage } from "./context"
import { enqueue, flushBeacon } from "./transport"
import { startEngagement, markInteraction } from "./engagedTime"
import { startClickCapture } from "./clicks"
import { startNetworkCapture } from "./networkCapture"
import { effect } from "@preact/signals-react"
import { identityKey } from "./identity"

let initialized = false

const nowMs = () => new Date().getTime()

// Emit user_signed_in from the identity signals — no component in the tree.
// A grace window after init swallows the no-user→user transition that happens when a
// returning session is restored from storage on page load, so we don't fire a spurious
// sign-in on every reload. A real login happens seconds later, well past the window.
// Identity rides on the envelope (user_id is stamped server-side in Dipi), so no props here.
//
// user_signed_out is NOT emitted here, unlike PUIG: Dipi clears its user state only after the
// logout request has been answered, so an event fired on that change can never be delivered
// (the cookie is already dead → 401). networkCapture.js emits it as the logout request goes out.
function startAuthTracking() {
    let primed = false
    let prev = null
    const graceUntil = nowMs() + 2000
    effect(() => {
        try {
            const current = identityKey()
            if (!primed) { prev = current; primed = true; return }
            if (nowMs() >= graceUntil && current && !prev) track("user_signed_in")
            prev = current
        } catch { /* fail-silent — never throw into the signals runtime */ }
    })
}

// Low-level emit: stamps session seq + envelope and enqueues. Does NOT touch session
// rotation, so it's safe to call from inside the rotation callback.
function emit(event, props, ts) {
    const session = touchSession(ts)
    enqueue(buildEnvelope(event, props, session, ts))
}

// Emitted automatically when a fresh session is minted (PRD §3.4). Server infers
// session_ended from inactivity gaps; we also emit one best-effort on unload.
function onSessionRotate(reason, prev, next) {
    emit("session_started", { reason }, next.startTs)
}

// The one public way to record an event.
export function track(event, props) {
    try {
        if (!config.enabled || !event) return
        const ts = nowMs()
        ensureSession(identityKey(), ts, onSessionRotate)
        markInteraction() // a programmatic event counts as activity (resets AFK idle)
        emit(event, props, ts)
    } catch {
        /* fail-silent */
    }
}

// Called by the router layer on every route entry. Keeps page context current and emits
// screen_view; the previous screen's engaged time is emitted by reportScreenLeave().
export function trackScreenView({ path, referrer, screen, domain }) {
    try {
        if (!config.enabled) return
        setPage({ path, referrer, screen, domain })
        track("screen_view", { screen, path })
    } catch { /* fail-silent */ }
}

export function trackTimeOnScreen({ screen, active_ms, total_ms }) {
    try {
        if (!config.enabled) return
        track("time_on_screen", { screen, active_ms, total_ms })
    } catch { /* fail-silent */ }
}

// Fires on pagehide. This is NOT a true session end — the page may just be reloading or
// navigating, and the session (keyed on inactivity) lives on across that. So we emit a
// `page_unloaded` marker and flush; the real session end is inferred server-side from the
// inactivity gap (PRD §3.4).
function onUnload() {
    try {
        const s = getCurrent()
        if (s) emit("page_unloaded", { duration_ms: nowMs() - s.startTs, events: s.seq }, nowMs())
        flushBeacon()
    } catch { /* fail-silent */ }
}

export function initAnalytics() {
    try {
        if (initialized || !config.enabled || typeof window === "undefined") return
        initialized = true
        startEngagement()
        startClickCapture(track)
        startNetworkCapture(track)
        startAuthTracking()
        // Flush + close the session when the tab is hidden or unloaded.
        window.addEventListener("pagehide", onUnload)
        document.addEventListener("visibilitychange", () => {
            if (document.visibilityState === "hidden") flushBeacon()
        })
        if (config.debug) console.debug("[analytics] initialized")
    } catch { /* fail-silent */ }
}
